import { useEffect, useRef } from 'react'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { Input } from '@heroui/react'

/**
 * ConditionalFieldInput: A reusable input for conditionally enabled fields in forms.
 * Handles auto-focus, clear, and error display for controlled fields.
 */
export interface ConditionalFieldInputProps<T extends FieldValues> {
	control: Control<T>
	name: FieldPath<T>
	label: string
	isEnabled: boolean
	isRequired?: boolean
	autoFocus?: boolean
	className?: string
	maxLength: number
}

export function ConditionalFieldInput<T extends FieldValues>({
	control,
	name,
	label,
	isEnabled,
	isRequired = false,
	autoFocus = false,
	className,
	maxLength,
}: ConditionalFieldInputProps<T>) {
	const inputRef = useRef<HTMLInputElement>(null)
	const wasEnabledRef = useRef(isEnabled)
	// useController gives us direct access to `field` so we can clear the value
	// when the input becomes disabled to ensure it isn't submitted.
	const { field, fieldState } = useController({ control, name })

	// Auto-focus only when field transitions from disabled to enabled
	useEffect(() => {
		if (isEnabled && !wasEnabledRef.current && autoFocus && inputRef.current) {
			const timer = setTimeout(() => {
				inputRef.current?.focus()
			}, 100)
			wasEnabledRef.current = isEnabled
			return () => clearTimeout(timer)
		}
		wasEnabledRef.current = isEnabled
	}, [isEnabled, autoFocus])

	// Clear the field value when the input becomes disabled.
	useEffect(() => {
		if (!isEnabled && wasEnabledRef.current) {
			// Only clear if there is a non-empty value
			if (typeof field.value === 'string' && field.value !== '') {
				field.onChange('')
			}
		}
		// keep the wasEnabledRef in sync for future transitions
		wasEnabledRef.current = isEnabled
	}, [isEnabled, field])

	return (
		<Input
			ref={inputRef}
			label={label}
			value={typeof field.value === 'string' ? field.value : ''}
			onChange={field.onChange}
			onBlur={field.onBlur}
			onClear={() => field.onChange('')}
			isDisabled={!isEnabled}
			isRequired={isRequired}
			isInvalid={isEnabled && !!fieldState.error}
			errorMessage={isEnabled ? fieldState.error?.message : undefined}
			isClearable
			className={className}
			maxLength={maxLength}
		/>
	)
}
